import { Box, Circle, Float, HStack, Icon, Text } from '@chakra-ui/react';
import { forwardRef } from 'react';
import { FcInfo, FcVoicePresentation } from 'react-icons/fc';
import { LuLogOut, LuUserRound } from 'react-icons/lu';
import type WebUser from 'src/auth/WebUser';
import { userService } from 'src/service/UserService';
import { useI18n } from 'src/i18n/I18nContext';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import type { ButtonProps } from '@/components/ui/button';
import {
    MenuContent,
    MenuItem,
    MenuRoot,
    MenuSeparator,
    MenuTrigger,
} from '@/components/ui/menu';
import { ChevronDownIcon } from '../icons/Icon';
import { HeaderDropdownItem, HeaderMenuIcon } from './NavigationPrimitives';
import type { HeaderLinkNode, HeaderMenuNode } from './navigation';

const PROFILE_MENU_NODES: HeaderLinkNode[] = [
    { nameKey: 'header.profile', link: '/profile', icon: FcVoicePresentation },
    { nameKey: 'header.info', link: '/info/', icon: FcInfo },
];

const LOGOUT_ICON_NODE: HeaderMenuNode = {
    nameKey: 'header.logout',
    icon: LuLogOut,
};

const getUserDisplayName = (user: WebUser): string =>
    [user.data.firstName, user.data.lastName].filter(Boolean).join(' ');

export interface MobileControlColors {
    color: string;
    background: string;
    borderColor: string;
    hoverBackground: string;
}

export const getMobileControlColors = (active: boolean): MobileControlColors => ({
    color: active ? 'blue.fg' : 'blue.contrast',
    background: active ? 'blue.subtle' : 'blue.solid',
    borderColor: active ? 'blue.border' : 'border.subtle',
    hoverBackground: active ? 'blue.muted' : 'blue.emphasized',
});

const OnlineAvatar = ({
    user,
    size,
}: {
    user: WebUser;
    size: 'sm' | 'md';
}) => {
    const fullName = getUserDisplayName(user);

    return (
        <Avatar
            size={size}
            name={fullName}
            shape='full'
            src={userService.avatarUrl(user)}
            flexShrink={0}
        >
            <Float placement='bottom-end' offsetX='1' offsetY='1'>
                <Circle
                    bg='green.solid'
                    size='8px'
                    outline='0.2em solid'
                    outlineColor='bg'
                />
            </Float>
        </Avatar>
    );
};

export const UserIdentityCard = ({
    user,
    marginBottom,
}: {
    user: WebUser;
    marginBottom?: string;
}) => {
    const fullName = getUserDisplayName(user);

    return (
        <HStack
            width='full'
            gap='3'
            p='3'
            mb={marginBottom}
            rounded='2xl'
            bg='blue.subtle'
            borderWidth='1px'
            borderColor='blue.border'
        >
            <OnlineAvatar user={user} size='md' />
            <Box minW='0'>
                <Text fontWeight='semibold' truncate>{fullName}</Text>
                <Text fontSize='sm' color='fg.muted' truncate>{user.data.email}</Text>
            </Box>
        </HStack>
    );
};

interface MobileProfileTriggerProps extends ButtonProps {
    active: boolean;
}

const MobileProfileTrigger = forwardRef<HTMLButtonElement, MobileProfileTriggerProps>(
    function MobileProfileTrigger({ active, ...props }, ref) {
        const { t } = useI18n();
        const colors = getMobileControlColors(active);

        return (
            <Button
                ref={ref}
                variant='ghost'
                boxSize='10'
                minW='10'
                rounded='lg'
                p='0'
                borderWidth='1px'
                borderColor={colors.borderColor}
                color={colors.color}
                bg={colors.background}
                _hover={{ bg: colors.hoverBackground }}
                aria-label={t('header.profile')}
                {...props}
            >
                <Icon boxSize='5' as={LuUserRound} />
            </Button>
        );
    }
);

interface DesktopProfileTriggerProps extends ButtonProps {
    active: boolean;
    user: WebUser;
}

const DesktopProfileTrigger = forwardRef<HTMLButtonElement, DesktopProfileTriggerProps>(
    function DesktopProfileTrigger({ active, user, ...props }, ref) {
        const { t } = useI18n();

        return (
            <Button
                ref={ref}
                variant='ghost'
                height='11'
                rounded='full'
                boxShadow='md'
                ps='1.5'
                pe='2.5'
                gap='2.5'
                borderWidth='1px'
                borderColor={active ? 'blue.border' : 'border.subtle'}
                color='fg'
                bg={active ? 'blue.subtle' : 'bg.panel'}
                _hover={{ bg: active ? 'blue.muted' : 'bg.subtle' }}
                aria-label={t('header.profile')}
                {...props}
            >
                <OnlineAvatar user={user} size='sm' />
                <Box textAlign='left' display={{ lg: 'none', xl: 'block' }}>
                    <Text maxW='8rem' truncate fontSize='sm' fontWeight='semibold' lineHeight='1.2'>
                        {user.data.firstName}
                    </Text>
                    <Text maxW='8rem' truncate fontSize='xs' color='fg.muted' lineHeight='1.2'>
                        {user.data.email}
                    </Text>
                </Box>
                <ChevronDownIcon />
            </Button>
        );
    }
);

interface ProfileMenuProps {
    user: WebUser;
    path: string;
    variant: 'desktop' | 'mobile';
    onNavigate: (link: string) => void;
    onLogout: () => void;
}

const ProfileMenu = ({
    user,
    path,
    variant,
    onNavigate,
    onLogout,
}: ProfileMenuProps) => {
    const { t } = useI18n();
    const active = ['profile', 'info'].includes(path);

    return (
        <MenuRoot positioning={{ placement: 'bottom-end', gutter: 8 }}>
            <MenuTrigger asChild>
                {variant === 'mobile'
                    ? <MobileProfileTrigger active={active} />
                    : <DesktopProfileTrigger active={active} user={user} />}
            </MenuTrigger>
            <MenuContent
                minW='17rem'
                p='2'
                rounded='xl'
                borderColor='border.subtle'
                bg='bg.panel'
                shadow='2xl'
            >
                <UserIdentityCard user={user} marginBottom='2' />
                <MenuSeparator />
                {PROFILE_MENU_NODES.map((node) => (
                    <HeaderDropdownItem
                        key={node.nameKey}
                        node={node}
                        path={path}
                        onNavigate={onNavigate}
                    />
                ))}
                <MenuSeparator />
                <MenuItem
                    value='logout'
                    rounded='lg'
                    px='2.5'
                    py='2'
                    gap='3'
                    color='red.fg'
                    _hover={{ bg: 'red.subtle' }}
                    onClick={onLogout}
                >
                    <HeaderMenuIcon node={LOGOUT_ICON_NODE} adminColor />
                    <Text flex='1' fontWeight='medium'>{t('header.logout')}</Text>
                </MenuItem>
            </MenuContent>
        </MenuRoot>
    );
};

export default ProfileMenu;
